import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Banknote, Car, ChevronDown, ChevronUp, Download, Info } from 'lucide-react';
import { SkeletonTableRows } from '../ui/kit';
import { berlinerBetriebswoche, betriebstagSchluessel, datumUndZeit } from '../lib/zeit';

const fmtEur = (v) => v == null
  ? '–'
  : `€${v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Ein nicht erfasster Wert ist NICHT null Euro und NICHT null Fahrten. Er bekommt „–",
// sonst liest der Partner „nichts verdient", wo in Wahrheit „nichts gemessen" steht.
const fmtNum = (v, d = 2) => v == null
  ? '–'
  : v.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (v) => (v == null ? '–' : `${v.toFixed(2)}%`);

// Default: laufende Woche (Mo–So)
// PARTNER-LEISTUNG-EINNAHMEN-UBER-001 (B2): Der Montag wird in ORTSZEIT bestimmt —
// dann darf er nicht in UTC formatiert werden. `toISOString().slice(0,10)` lieferte
// zwischen 00:00 und 02:00 Berliner Zeit den VORTAG, die Seite zeigte also nachts eine
// um einen Tag verschobene Woche. `toLocaleDateString('en-CA')` ist die im Haus
// begründete Lösung (admin-app/src/pages/Invoices.js:14-20).
export const tagesString = (date) => betriebstagSchluessel(date);

export function defaultRange(now = new Date()) {
  return berlinerBetriebswoche(now);
}

export default function Performance() {
  const [tab, setTab] = useState('drivers'); // 'drivers' | 'vehicles'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [range, setRange] = useState(defaultRange());
  const [tipDriver,setTipDriver]=useState(null);
  const [helpFor, setHelpFor] = useState(null);   // Spalte, deren Erklärung offen steht
  const [sortKey, setSortKey] = useState('total_earnings');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/partners/performance', { params: { from: range.from, to: range.to } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const rows = (tab === 'drivers' ? data?.drivers : data?.vehicles) || [];
  const filtered = rows.filter(r => r.name?.toLowerCase().includes(search.toLowerCase()) || r.license_plate?.toLowerCase().includes(search.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  // Jede Spalte sagt selbst, was sie bedeutet — der Partner soll nicht raten müssen.
  const driverCols = [
    { key: 'name', label: 'Name', align: 'left' },
    { key: 'total_earnings', label: 'Partnerumsatz', fmt: fmtEur,
      help: 'Was von den Fahrten dieses Fahrers bei dir als Partner ankommt: der Fahrpreis abzüglich VOOGO-Anteil. Kein Auszahlungsbetrag.' },
    { key: 'bonus_information', label: 'Bonusgutschrift Fahrer', fmt: fmtEur,
      help: 'Tatsächlich im Finanzbuch erfasster Bonus. Vom Partner an den Fahrer zu zahlen; kein zusätzlicher Partnerumsatz und keine bestätigte Bankauszahlung.' },
    { key: 'tip_information', label: 'Trinkgeld', fmt: fmtEur,
      help: 'Trinkgeldbeträge aus dem Finanzbuch. Gegenbuchungen werden getrennt in den Details ausgewiesen; sie verändern nicht rückwirkend diese Informationssumme. Kein geschätztes Bartrinkgeld und keine Auszahlungssumme.' },
    { key: 'total_including_bonus_and_tips', label: 'Fahrtumsatz + Bonus + Trinkgeld', fmt: fmtEur,
      help: 'Rechnerische Summe aus Partnerumsatz, Fahrerbonus und Trinkgeld, jeweils einmal gezählt. Kein Partnererlös und kein Auszahlungsbetrag.' },
    { key: 'cash_earnings', label: 'Kundeneinzug Bar', fmt: fmtEur,
      help: 'Der Teil des Fahrpreises, den der Fahrer bar vom Kunden kassiert hat. Das Geld liegt beim Fahrer, nicht bei VOOGO — es wird bei der Abrechnung gegengerechnet.' },
    { key: 'trips_per_hour', label: 'Fahrten / Std', fmt: (v) => fmtNum(v, 2),
      help: 'Abgeschlossene Fahrten je Stunde im Dienst. Zeigt, wie gut der Fahrer ausgelastet war. „–“, solange keine Dienstzeit erfasst ist.' },
    { key: 'hours_online', label: 'Stunden online', fmt: (v) => fmtNum(v, 2),
      help: 'Zeit, in der der Fahrer in der App dienstbereit war und Fahrten bekommen konnte — nicht die Zeit hinter dem Steuer.' },
    { key: 'trips_taken', label: 'Fahrten', fmt: (v) => v,
      help: 'Abgeschlossene Fahrten im gewählten Zeitraum.' },
    { key: 'acceptance', label: 'Annahme', fmt: fmtPct,
      help: 'Anteil der angebotenen Fahrten, die der Fahrer angenommen hat.' },
    { key: 'cancellation', label: 'Storno', fmt: fmtPct,
      help: 'Anteil der Fahrten, die nach der Annahme nicht zu Ende gefahren wurden.' },
  ];
  const vehicleCols = [
    { key: 'name', label: 'Fahrzeug', align: 'left' },
    { key: 'license_plate', label: 'Kennzeichen', align: 'left', fmt: (v) => v },
    { key: 'total_earnings', label: 'Partnerumsatz', fmt: fmtEur,
      help: 'Bleibt „–“: Geld lässt sich rückwirkend nicht beweisbar einem Fahrzeug zuordnen, weil dafür der unveränderliche Fahrzeug-Schnappschuss fehlt.' },
    { key: 'cash_earnings', label: 'Kundeneinzug Bar', fmt: fmtEur,
      help: 'Bleibt „–“: siehe Partnerumsatz — ohne Fahrzeug-Schnappschuss keine beweisbare Zuordnung.' },
    { key: 'driving_hours', label: 'Fahrtzeit', fmt: (v) => fmtNum(v, 2),
      help: 'Summe der Fahrtdauern mit diesem Fahrzeug. Ein Fahrzeug geht nicht „online“ — dienstbereit ist der Fahrer, deshalb steht hier die Fahrtzeit.' },
    { key: 'trips_per_hour', label: 'Fahrten / Fahrtstunde', fmt: (v) => fmtNum(v, 2),
      help: 'Abgeschlossene Fahrten je Stunde Fahrtzeit dieses Fahrzeugs.' },
    { key: 'trips_taken', label: 'Fahrten', fmt: (v) => v,
      help: 'Abgeschlossene Fahrten mit diesem Fahrzeug im gewählten Zeitraum.' },
  ];
  const cols = tab === 'drivers' ? driverCols : vehicleCols;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const downloadCsv = () => {
    const header = cols.map(c => c.label);
    const lines = sorted.map(r => cols.map(c => r[c.key] ?? ''));
    const csv = [header, ...lines].map(l => l.map(x => `"${x}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `leistung-${tab}-${range.from}_${range.to}.csv`; a.click();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          {data?.partner_name && (
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563', marginBottom: 2 }}>{data.partner_name}</div>
          )}
          <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -1 }}>Leistung</h1>
        </div>
        <button onClick={downloadCsv} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#f3f4f6', borderRadius: 22, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
          <Download size={15} /> Download
        </button>
      </div>
      <p style={{ color: '#4b5563', fontSize: 13, margin: '-10px 0 18px' }}>
        Geldwerte stammen aus den nachträglich unveränderbaren Buchungen und sind keine Auszahlung.
        Fahrzeug-Geldwerte bleiben ohne unveränderlichen Fahrzeug-Schnappschuss bewusst „–“
        — gemeint ist die bei der Fahrt festgehaltene Fahrzeugzuordnung, kein hochgeladenes Dokument.
        „Stunden online“ ist die dienstbereite Zeit in der Fahrer-App, nicht die Fahrtdauer;
        ohne erfasste Dienstzeit stehen die Pro-Stunde-Werte auf „–“ statt auf null.
      </p>

      {/* Boost cards */}
      <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Steigere die Leistung deiner Flotte</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 360px))', gap: 16, marginBottom: 30 }}>
        {[
          { icon: Banknote, title: 'Bargeld-Limit', desc: 'Lege Limits für Barzahlungen deiner gesamten Flotte fest', to: '/balance' },
          { icon: Car, title: 'Auto-Annahme', desc: 'Fahrtanfragen automatisch im Namen der Fahrer annehmen', to: '/drivers' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: 20 }}>
            <Icon size={22} />
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 10 }}>{title}</div>
            <div style={{ fontSize: 13, color: '#4b5563', marginTop: 4, marginBottom: 16, lineHeight: 1.4 }}>{desc}</div>
            <button style={{ width: '100%', padding: '10px', background: '#f3f4f6', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>Verwalten</button>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 18 }}>
        {[['drivers', 'Fahrer'], ['vehicles', 'Fahrzeuge']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ flex: 1, padding: '12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: tab === k ? '#000' : '#6b7280', borderBottom: tab === k ? '3px solid #000' : '3px solid transparent', marginBottom: -1 }}>
            {l}
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen"
          style={{ flex: 1, minWidth: 200, padding: '11px 16px', borderRadius: 24, border: 'none', background: '#f3f4f6', fontSize: 14 }} />
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Startdatum</div>
          <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
            style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Enddatum</div>
          <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
            style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13 }} />
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              {cols.map(c => (
                <th key={c.key} onClick={() => toggleSort(c.key)} title={c.help || undefined}
                  style={{ padding: '12px 14px', fontSize: 12, color: '#4b5563', fontWeight: 700, textAlign: c.align || 'right', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {c.label}
                  {c.help && (
                    <button type="button" aria-label={`${c.label}: ${c.help}`} title={c.help}
                      onClick={(e) => { e.stopPropagation(); setHelpFor(k => k === c.key ? null : c.key); }}
                      style={{ background: 'none', border: 'none', padding: '0 0 0 4px', cursor: 'help', verticalAlign: -2, color: helpFor === c.key ? '#111' : '#6b7280' }}>
                      <Info size={12} aria-hidden="true" />
                    </button>
                  )}
                  {sortKey === c.key ? (sortDir === 'asc' ? <ChevronUp size={12} style={{ verticalAlign: -2 }} /> : <ChevronDown size={12} style={{ verticalAlign: -2 }} />) : ''}
                </th>
              ))}
            </tr>
            {helpFor && (
              <tr>
                <td colSpan={cols.length} style={{ padding: '10px 14px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#374151', lineHeight: 1.45, textAlign: 'left' }}>
                    <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                    <span><strong>{cols.find(c => c.key === helpFor)?.label}:</strong>{' '}
                      {cols.find(c => c.key === helpFor)?.help}</span>
                  </div>
                </td>
              </tr>
            )}
          </thead>
          <tbody>
            {loading && <SkeletonTableRows rows={5} cols={cols.length} />}
            {!loading && sorted.length === 0 && <tr><td colSpan={cols.length} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Keine Daten im gewählten Zeitraum</td></tr>}
            {!loading && sorted.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                {cols.map((c, i) => (
                  <td key={c.key} style={{ padding: '14px', fontSize: 14, textAlign: c.align || 'right', whiteSpace: 'nowrap', color: i === 0 ? '#000' : '#374151', fontWeight: i === 0 ? 600 : 400 }}>
                    {i === 0 && tab === 'drivers' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#4b5563' }}>{r.name?.charAt(0)}</div>
                        {r.name}
                      </div>
                    ) : c.key==='tip_information'?<button type="button" onClick={()=>setTipDriver({id:r.id,name:r.name})} aria-label={`Trinkgeldbuchungen ${r.name}`}>{fmtEur(r.tip_information)}</button>:(c.fmt ? c.fmt(r[c.key]) : r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tipDriver&&<TipDetails driver={tipDriver} range={range} onClose={()=>setTipDriver(null)}/>}
    </div>
  );
}

function TipDetails({driver,range,onClose}) {
 const [data,setData]=useState(null),[error,setError]=useState(false);
 useEffect(()=>{let active=true;setData(null);setError(false);
  api.get(`/partners/driver/${encodeURIComponent(driver.id)}/earnings`,{params:{from:range.from,to:range.to}})
   .then(r=>{if(active)setData(r.data);}).catch(()=>{if(active)setError(true);});
  return()=>{active=false;};
 },[driver.id,range.from,range.to]);
 return <section aria-label="Trinkgeldbuchungen" style={{marginTop:20,padding:16,border:'1px solid #d1d5db',borderRadius:8}}>
  <h3>Trinkgeld · {driver.name}</h3><p>{range.from} bis {range.to} · ursprüngliche Trinkgeldbeträge; Gegenbuchungen mit ihrer eigenen Buchwirkung.</p>
  <button type="button" onClick={onClose}>Details schließen</button>
  {error?<p role="alert">Buchungen konnten nicht geladen werden.</p>:!data?<p>Buchungen laden…</p>:<>
   <p>Trinkgeld gesamt: {fmtEur(data.breakdown?.tips)}</p>
   {!Array.isArray(data.tip_entries)?<p>Einzelbuchungen nicht verfügbar.</p>:!data.tip_entries.length?<p>Keine Trinkgeldbuchungen in diesem Zeitraum.</p>:<table><thead><tr><th>Zeitpunkt</th><th>Fahrt</th><th>Buchung</th><th>Trinkgeld (Information)</th><th>Buchwirkung</th></tr></thead><tbody>
    {data.tip_entries.map(e=><tr key={e.entry_id}><td>{datumUndZeit(e.occurred_at)}</td><td>{e.ride_id||'Ohne Fahrtzuordnung'}</td><td>{e.entry_id}{e.reversal_of&&<> · Gegenbuchung zu {e.reversal_of}</>}</td><td>{fmtEur(e.tip_cents==null?null:e.tip_cents/100)}</td><td>{fmtEur(e.ledger_effect_cents/100)}</td></tr>)}
   </tbody></table>}
  </>}
 </section>;
}
